import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert, Package, RefreshCw, Save, Mail, Euro, TrendingUp, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatEUR } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const FULFILLMENT = [
  { value: "processing", label: "En préparation" },
  { value: "shipped", label: "Expédiée" },
  { value: "delivered", label: "Livrée" },
];

const PAY_BADGE = {
  paid: "text-green-400 border-green-500/30 bg-green-500/10",
  pending: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  failed: "text-red-400 border-red-500/30 bg-red-500/10",
  expired: "text-stone-400 border-white/10 bg-white/5",
};

function OrderRow({ order, onSaved }) {
  const [status, setStatus] = useState(order.fulfillment_status || "processing");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [saving, setSaving] = useState(false);
  const isPaid = order.payment_status === "paid";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.order_number}`, {
        fulfillment_status: status,
        tracking_number: tracking,
      });
      toast.success(
        status === "shipped"
          ? "Commande marquée expédiée — email client envoyé ✉️"
          : "Commande mise à jour"
      );
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#120E1A] border border-amber-900/20 rounded-xl p-5" data-testid={`admin-order-${order.order_number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-mono-tech text-lg font-black text-amber-400">{order.order_number}</p>
          <p className="text-sm text-stone-400 flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> {order.email || "—"}
          </p>
          <p className="text-xs text-stone-500 mt-0.5">
            {order.created_at ? new Date(order.created_at).toLocaleString("fr-FR") : ""}
          </p>
        </div>
        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${PAY_BADGE[order.payment_status] || PAY_BADGE.expired}`}>
          {order.payment_status === "paid" ? "Payée" : order.payment_status}
        </span>
      </div>

      <div className="border-t border-amber-900/20 pt-3 space-y-1.5 mb-4">
        {order.items.map((it, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-stone-300">{it.name} <span className="text-stone-500">× {it.quantity}</span></span>
            {it.amount && <span className="font-mono-tech text-stone-300">{it.amount}</span>}
          </div>
        ))}
        <div className="flex justify-between text-sm pt-1.5 border-t border-amber-900/10">
          <span className="text-stone-400">Livraison : {order.shipping}</span>
          <span className="font-mono-tech font-bold text-amber-400">{order.total}</span>
        </div>
      </div>

      {isPaid ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs text-stone-400 mb-1">Statut d'expédition</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid={`admin-status-select-${order.order_number}`}
              className="w-full rounded-lg bg-[#08060B] border border-white/10 px-3 py-2 text-stone-100 text-sm outline-none focus:border-amber-500"
            >
              {FULFILLMENT.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs text-stone-400 mb-1">Numéro de suivi (optionnel)</label>
            <input
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              placeholder="Ex. 6A12345678901"
              data-testid={`admin-tracking-input-${order.order_number}`}
              className="w-full rounded-lg bg-[#08060B] border border-white/10 px-3 py-2 text-stone-100 text-sm outline-none focus:border-amber-500"
            />
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid={`admin-save-${order.order_number}`}
            className="btn-ember rounded-lg px-5 py-2 flex items-center gap-2 text-sm disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </div>
      ) : (
        <p className="text-sm text-stone-500 italic">Commande non payée — aucune action d'expédition.</p>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, id }) {
  return (
    <div className="bg-[#120E1A] border border-amber-900/20 rounded-xl p-4" data-testid={`stat-${id}`}>
      <div className="flex items-center gap-2 text-stone-400 text-xs mb-2">
        <Icon className={`h-4 w-4 ${accent}`} /> {label}
      </div>
      <p className={`font-mono-tech text-xl sm:text-2xl font-extrabold ${accent}`}>{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    setError("");
    api
      .get("/admin/orders")
      .then((r) => setOrders(r.data))
      .catch((err) => {
        setError(formatApiErrorDetail(err?.response?.data?.detail) || "Erreur de chargement");
        setOrders([]);
      });
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center">
        <Loader2 className="h-10 w-10 text-amber-500 mx-auto animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="admin-denied">
        <ShieldAlert className="h-16 w-16 text-red-500 mx-auto mb-6" />
        <h1 className="font-display text-3xl text-stone-100 mb-3">Accès réservé</h1>
        <p className="text-stone-400 mb-8">Cette page est réservée à l'administrateur de la boutique.</p>
        <Link to="/connexion" className="btn-ember rounded-xl px-6 py-3">Se connecter</Link>
      </div>
    );
  }

  const paidCount = (orders || []).filter((o) => o.payment_status === "paid").length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-16" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-black text-stone-50 flex items-center gap-3">
            <Package className="h-8 w-8 text-amber-500" /> Commandes
          </h1>
          <p className="text-stone-400 mt-1">
            {orders ? `${orders.length} commande(s) · ${paidCount} payée(s)` : "Chargement…"}
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-refresh"
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-900/40 text-stone-300 hover:border-amber-600 text-sm"
        >
          <RefreshCw className="h-4 w-4" /> Rafraîchir
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="admin-stats">
          <StatCard id="revenue" icon={Euro} label="Chiffre d'affaires" value={formatEUR(stats.revenue)} sub={`${stats.paid_orders} commande(s) payée(s)`} accent="text-amber-400" />
          <StatCard id="month" icon={TrendingUp} label="CA ce mois-ci" value={formatEUR(stats.revenue_month)} sub={`${stats.orders_month} commande(s)`} accent="text-green-400" />
          <StatCard id="avg" icon={Package} label="Panier moyen" value={formatEUR(stats.avg_basket)} sub={`Aujourd'hui : ${formatEUR(stats.revenue_today)}`} accent="text-fuchsia-400" />
          <StatCard id="toship" icon={Clock} label="À expédier" value={stats.to_process} sub="en préparation" accent="text-orange-400" />
        </div>
      )}

      {error && <p className="text-red-400 mb-6">{error}</p>}

      {orders === null ? (
        <div className="py-20 text-center"><Loader2 className="h-8 w-8 text-amber-500 mx-auto animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center text-stone-500" data-testid="admin-empty">Aucune commande pour le moment.</div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <OrderRow
              key={o.order_number}
              order={o}
              onSaved={(updated) =>
                setOrders((prev) => prev.map((x) => (x.order_number === updated.order_number ? updated : x)))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
